/**
 * @file Components/Ui/TourCard.jsx
 * @description Reusable tour card UI component. Renders tour preview with
 * image, title, price, rating, duration, category badge, group pricing,
 * difficulty indicator, and wishlist toggle button.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, Users, Clock, Star, Tag } from 'lucide-react';

const categoryColors = {
  customized: 'bg-purple-500 text-white',
  educational: 'bg-green-500 text-white',
  adventurous: 'bg-orange-500 text-white',
  luxury: 'bg-amber-500 text-white',
  family: 'bg-pink-500 text-white',
  corporate: 'bg-slate-600 text-white',
  adventure: 'bg-orange-500 text-white',
  cultural: 'bg-indigo-500 text-white',
  beach: 'bg-cyan-500 text-white',
  mountain: 'bg-emerald-500 text-white',
  wildlife: 'bg-lime-600 text-white',
  city: 'bg-blue-500 text-white',
};

const difficultyBadge = {
  easy: 'bg-green-100 text-green-700',
  moderate: 'bg-yellow-100 text-yellow-700',
  challenging: 'bg-red-100 text-red-700',
};

const TourCard = ({ tour }) => {
  const {
    _id,
    title = 'Untitled Tour',
    description = 'No description available',
    location = 'Location not specified',
    duration = 0,
    groupSize = 0,
    startDates = [],
    price = 0,
    pricing,
    thumbnail = '',
    images = [],
    rating,
    category = 'uncategorized',
    difficulty,
    featured = false,
    itinerary = [],
  } = tour || {};

  const hasGroupPrice = pricing?.group && pricing.group < price;
  const nearestStartDate = startDates.length > 0
    ? new Date(startDates[0]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Date TBA';
  const defaultImage = 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80';

  return (
    <div className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 flex flex-col transform hover:-translate-y-2 border border-gray-100">
      {/* Image Container */}
      <div className="relative h-72 overflow-hidden">
        <img
          src={thumbnail || images[0] || defaultImage}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          onError={(e) => { e.target.onerror = null; e.target.src = defaultImage; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

        {featured && (
          <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center space-x-1 animate-pulse">
            <Star size={12} className="fill-current" />
            <span>Featured</span>
          </div>
        )}

        {/* Category badge */}
        <div className="absolute top-4 left-4 flex items-center gap-1.5">
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full shadow-md ${categoryColors[category] || 'bg-gray-500 text-white'}`}>
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </span>
          {difficulty && (category === 'adventurous' || category === 'adventure') && (
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${difficultyBadge[difficulty] || difficultyBadge.moderate}`}>
              {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
            </span>
          )}
        </div>

        {/* Itinerary count badge */}
        {itinerary.length > 0 && (
          <div className="absolute bottom-16 right-4 bg-white/90 backdrop-blur text-xs font-semibold text-gray-700 px-2.5 py-1 rounded-full shadow">
            {itinerary.length}-day itinerary
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-white font-bold text-2xl line-clamp-2 drop-shadow-lg">{title}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-grow bg-gradient-to-b from-white to-gray-50">
        {/* Location and Rating */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-gray-700 bg-blue-50 px-3 py-1.5 rounded-full">
            <MapPin size={14} className="mr-1.5 text-blue-600" />
            <span className="text-sm font-medium truncate max-w-[140px]">{location}</span>
          </div>
          {typeof rating === 'number' && (
            <div className="flex items-center bg-yellow-50 px-3 py-1.5 rounded-full">
              <Star size={14} className="text-yellow-500 fill-current mr-1" />
              <span className="text-sm font-bold text-yellow-700">{rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        <p className="text-gray-600 text-sm line-clamp-2 mb-5 leading-relaxed">{description}</p>

        {/* Tour Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between text-sm p-3 bg-white rounded-lg border border-gray-100">
            <div className="flex items-center text-gray-600">
              <Calendar size={16} className="mr-2 text-blue-500" />
              <span className="font-medium">{nearestStartDate}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Clock size={16} className="mr-2 text-green-500" />
              <span className="font-medium">{duration > 0 ? `${duration} days` : 'TBA'}</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm p-3 bg-white rounded-lg border border-gray-100">
            <div className="flex items-center text-gray-600">
              <Users size={16} className="mr-2 text-purple-500" />
              <span className="font-medium">{groupSize > 0 ? `Max ${groupSize}` : 'TBA'}</span>
            </div>
            <div className="text-right">
              <div className="font-bold text-[#243185] text-base">
                {price > 0 ? `₹${price.toLocaleString()}` : 'TBA'}
              </div>
              {hasGroupPrice && (
                <div className="flex items-center gap-1 text-[10px] text-green-600 font-medium">
                  <Tag size={10} />
                  Group: ₹{pricing.group.toLocaleString()}/pp
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <Link
          to={`/tours/${_id}`}
          className="mt-auto text-center bg-gradient-to-r from-[#243185] to-[#1a2460] text-white py-3.5 rounded-xl font-semibold hover:from-[#1a2460] hover:to-[#0f1740] transition-all duration-300 shadow-md hover:shadow-xl transform hover:scale-[1.02]"
        >
          <span className="flex items-center justify-center">
            View Details
            <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </span>
        </Link>
      </div>
    </div>
  );
};

export default TourCard;
